import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import BetaEnrichmentFunnel from 'components/upb_viewer_beta/BetaEnrichmentFunnel';
import BetaPaneSelect from 'components/upb_viewer_beta/BetaPaneSelect';
import useUPBBetaEnrichmentFunnel from 'components/upb_viewer_beta/hooks/useUPBBetaEnrichmentFunnel';
import {
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_TITLE,
  FONT_WEIGHT_EXTRABOLD,
  RADIUS_LG,
  RADIUS_MD,
  SHADOW_XS,
  SKELETON_BG,
  SPACE_3,
  WHITE,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaFunnelCard — pane-head + funnel card wrapper for the SKU Enrichment Funnel.
 *
 * Props:
 *   l1CategoryId  number | null   — currently selected L1 (null = all)
 *   vendorId      number | null   — distributor filter (null = all)
 */
function BetaFunnelCard({
  l1CategoryId,
  onL1Change,
  vendorId,
  thirdPartyTag,
  categories,
  skuType,
  skuTypeOptions,
  onSkuTypeChange,
}) {
  const { data: stages, loading } = useUPBBetaEnrichmentFunnel({
    l1CategoryId,
    vendorId,
    thirdPartyTag,
    skuType,
  });

  const categoryOptions = [
    { value: null, label: 'All L1 Categories', crumb: `${categories.length} L1` },
    ...categories.map(c => ({
      value: c.id,
      label: c.taxonomy_display_name || c.category_name,
      crumb: 'L1',
    })),
  ];

  return (
    <div className={css(styles.wrapper)}>
      {/* ── Pane head card ──────────────────────────────────────────────── */}
      <div className={css(styles.paneHead)}>
        <div>
          <h2 className={css(styles.paneTitle)}>SKU Enrichment Funnel</h2>
          <p className={css(styles.paneSub)}>
            From raw catalog to e&#8209;commerce ready. Track how SKUs progress through
            classification, mapping, and attribute scoring — and where they fall out.
          </p>
        </div>
        <div className={css(styles.paneActions)}>
          <BetaPaneSelect
            caption="SKU type"
            options={skuTypeOptions}
            value={skuType}
            onChange={onSkuTypeChange}
            minWidth={180}
            menuWidth={240}
          />
          <BetaPaneSelect
            caption="Category"
            options={categoryOptions}
            value={l1CategoryId}
            onChange={onL1Change}
            searchable
            minWidth={240}
            menuWidth={360}
          />
        </div>
      </div>

      {/* ── Funnel card ─────────────────────────────────────────────────── */}
      <div className={css(styles.funnelCard)}>
        <div className={css(styles.funnelBody)}>
          {loading ? (
            <div className={css(styles.loadingRow)}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                <div key={n} className={css(styles.skeleton)} />
              ))}
            </div>
          ) : (
            <BetaEnrichmentFunnel stages={stages || []} />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },

  // ─── Pane head ─────────────────────────────────────────────────────────────
  paneHead: {
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: '18px 22px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },

  paneActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },

  paneTitle: {
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_EXTRABOLD,
    fontSize: 24,
    lineHeight: 1.2,
    color: FG_1,
    letterSpacing: '-0.01em',
    margin: 0,
  },

  paneSub: {
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: 13,
    color: FG_3,
    marginTop: 4,
    maxWidth: 560,
    lineHeight: 1.5,
  },

  // ─── Funnel card ───────────────────────────────────────────────────────────
  funnelCard: {
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    overflow: 'hidden',
  },

  funnelBody: {
    padding: '24px 28px 30px',
  },

  // ─── Loading skeleton ──────────────────────────────────────────────────────
  loadingRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE_3,
  },

  skeleton: {
    height: 52,
    borderRadius: RADIUS_MD,
    backgroundColor: SKELETON_BG,
    animationName: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
    animationDuration: '1.4s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
});

BetaFunnelCard.propTypes = {
  l1CategoryId: PropTypes.number,
  onL1Change: PropTypes.func,
  vendorId: PropTypes.number,
  thirdPartyTag: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.object),
  skuType: PropTypes.string,
  skuTypeOptions: PropTypes.arrayOf(PropTypes.object),
  onSkuTypeChange: PropTypes.func,
};

BetaFunnelCard.defaultProps = {
  l1CategoryId: null,
  onL1Change: () => {},
  vendorId: null,
  thirdPartyTag: null,
  categories: [],
  skuType: 'active',
  skuTypeOptions: [],
  onSkuTypeChange: () => {},
};

export default BetaFunnelCard;
